import { convertCoverageLinesTextToEnum } from '@common/utils';
import { isArray, isEmpty, isEqual, keyBy } from 'lodash';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom-latest';
import { AppBar, BoxSkeleton, Button, Divider, Paper, SearchBar, Stack, Typography } from '@common-components';
import { LocalStorage } from 'enums';
import { useLocalStorage, useSearchCarrierSubmissions, useSearchMarket, useSearchSubmissionExtractedData } from 'hooks';
import { messages } from 'i18n';
import { CarrierSubmissionSearchItem } from 'types';
import { demoData, scrollToElementPosition } from 'utils';
import { BROKER_ROUTES } from 'broker/broker-routes';
import PageHeader from 'broker/components/common/layout/PageHeader';
import NoItemsFound from 'broker/components/common/NoItemsFound';
import TablePagination from 'broker/components/common/TablePagination';
import SubmissionFilter from 'broker/components/SubmissionsView/SubmissionFilter';
import SubmissionViewNoResults from 'broker/components/SubmissionsView/SubmissionViewNoResults';
import MainScrollArea from 'broker/hoc/MainScrollArea';
import QueueTableHeader from './QueueTableHeader';
import QueueTableItem from './QueueTableItem';
import useQueueFilters, { QueueFilterType } from './useQueueFilters';

const MainScrollAreaId = 'queueDataGridScrollArea';

export default function SubmissionQueuePage() {
  const navigate = useNavigate();
  const { items: eligibleMarkets, isLoading: isLoadingMarkets } = useSearchMarket();

  const [selectedMarketId] = useLocalStorage<string | null>(LocalStorage.OpExSelectedMarketId, null);

  const selectedMarket = useMemo(
    () => (selectedMarketId ? eligibleMarkets.find((m) => m.id === selectedMarketId) ?? null : null),
    [eligibleMarkets, selectedMarketId],
  );

  const shouldFetch = !!selectedMarketId;
  const {
    items: carrierSubmissions,
    count,
    isLoading,
    isFetching,
    isError: isSubmissionsError,
    error: submissionsError,
  } = useSearchCarrierSubmissions({
    enabled: shouldFetch,
    filter: shouldFetch ? { marketId: selectedMarketId! } : undefined,
  });

  const {
    items: submissionExtractions,
    isLoading: isLoadingExtractions,
    isFetching: isFetchingExtractions,
  } = useSearchSubmissionExtractedData({
    enabled: shouldFetch,
  });

  const extractionsBySubmissionId = useMemo(
    () => keyBy(submissionExtractions, 'submissionId'),
    [submissionExtractions],
  );

  const enrichedItems = useMemo<CarrierSubmissionSearchItem[]>(
    () =>
      carrierSubmissions.map((item) => {
        const extractedData = extractionsBySubmissionId[item.submissionId]?.extractedData;
        if (!extractedData) {
          return item;
        }
        return {
          ...item,
          submission: {
            ...item.submission,
            insuredName: item.submission?.insuredName ?? extractedData?.insuredName?.value,
            coverageLines:
              isEmpty(item.submission?.coverageLines) && isArray(extractedData?.coverageLines?.value)
                ? convertCoverageLinesTextToEnum(extractedData!.coverageLines!.value!)
                : item.submission?.coverageLines,
          },
        };
      }),
    [carrierSubmissions, extractionsBySubmissionId],
  );

  const {
    filters,
    filteredItems,
    search,
    setSearch,
    isFilterApplied,
    sortType,
    setSortType,
    paginationReset,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useQueueFilters(enrichedItems, isLoading || isLoadingExtractions, isFetching || isFetchingExtractions);

  const itemsSlice = filteredItems.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
    scrollToElementPosition(MainScrollAreaId, { top: 0, left: 0 });
  };

  const onSwitchCarrier = () => navigate(`/${BROKER_ROUTES.SUBMISSION_SERVICES}`);

  if (!isLoadingMarkets && !selectedMarket) {
    navigate(`/${BROKER_ROUTES.SUBMISSION_SERVICES}`, { replace: true });
    return null;
  }

  return (
    <Stack height={1}>
      <AppBar
        position="static"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'common.white',
          color: 'text.primary',
          px: 3,
          py: 1,
        }}
        elevation={0}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          {selectedMarket ? (
            <Stack direction="row" gap={0.5}>
              <Typography variant="body2">{messages.submissionQueuePage.workingOn}</Typography>
              <Typography variant="body2Bold">{selectedMarket.organizationName}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2">{messages.general.loading}</Typography>
          )}
          <Button variant="outlined" onClick={onSwitchCarrier} size="small">
            {messages.submissionQueuePage.switchCarrier}
          </Button>
        </Stack>
      </AppBar>

      <PageHeader>
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Typography variant="h4">{messages.submissionQueuePage.title}</Typography>
          <Typography color="text.secondary" variant="body2">
            {isFilterApplied || search.length > 1
              ? messages.general.outOfSubmissions(filteredItems.length.toString(), count.toString())
              : count}
          </Typography>
        </Stack>
      </PageHeader>

      <Stack flex={1} p={3} gap={2}>
        {(isLoading || carrierSubmissions.length > 0) && (
          <Stack direction="row" gap={1} alignItems="center">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={messages.submissionQueuePage.searchPlaceholder}
              id="opex-carrier-submissions-search"
              sx={{ width: 290 }}
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.Insured]}
              title={messages.submissionQueuePage.filterHeaders.insured}
              showSearch
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.Lob]}
              title={messages.submissionQueuePage.filterHeaders.coverageLines}
              showSearch
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.EffectiveDate]}
              title={messages.submissionQueuePage.filterHeaders.effectiveDate}
            />
            {isFilterApplied && (
              <Button
                onClick={() => {
                  Object.values(filters).forEach((filter) => {
                    filter.handleSelectAll();
                    paginationReset();
                  });
                }}
              >
                {messages.buttons.clearFilters}
              </Button>
            )}
          </Stack>
        )}

        {isSubmissionsError && (
          <Stack p={3} gap={1}>
            <Typography variant="body1">{messages.submissionQueuePage.loadError}</Typography>
            <Typography variant="body2" color="text.secondary">
              {submissionsError instanceof Error ? submissionsError.message : messages.submissionQueuePage.unknownError}
            </Typography>
          </Stack>
        )}

        {!isSubmissionsError && (isLoading || carrierSubmissions.length > 0) ? (
          <Stack component={Paper} variant="outlined" overflow="hidden" flex="1 1 0">
            <MainScrollArea
              id={MainScrollAreaId}
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
              }}
            >
              <QueueTableHeader sortType={sortType} setSortType={setSortType} />
              {isLoading ? (
                <Stack gap={2} p={2}>
                  {demoData(10).map((row, index) => (
                    <Stack gap={2} key={row.id}>
                      <BoxSkeleton isLoading sx={{ width: 1, height: 45 }} index={index} />
                      <Divider />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                itemsSlice.map((item) => {
                  const extractedData = extractionsBySubmissionId[item.submissionId]?.extractedData;
                  const isInsuredNameExtracted = !!(
                    extractedData?.insuredName?.value &&
                    item.submission?.insuredName === extractedData.insuredName.value
                  );
                  const isCoverageLinesExtracted =
                    isArray(extractedData?.coverageLines?.value) &&
                    !isEmpty(extractedData?.coverageLines?.value) &&
                    isEqual(
                      item.submission?.coverageLines,
                      convertCoverageLinesTextToEnum(extractedData!.coverageLines!.value!),
                    );
                  return (
                    <QueueTableItem
                      key={item.id}
                      item={item}
                      isInsuredNameExtracted={isInsuredNameExtracted}
                      isCoverageLinesExtracted={isCoverageLinesExtracted}
                      searchInput={search}
                    />
                  );
                })
              )}
              {!isLoading && itemsSlice.length === 0 && <SubmissionViewNoResults />}
            </MainScrollArea>
            {filteredItems.length > rowsPerPage && (
              <>
                <Divider />
                <TablePagination
                  from={page * rowsPerPage - rowsPerPage + 1}
                  to={page * rowsPerPage}
                  outOf={filteredItems.length}
                  handleChange={handlePageChange}
                  count={Math.ceil(filteredItems.length / rowsPerPage)}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                />
              </>
            )}
          </Stack>
        ) : (
          !isSubmissionsError && !isLoading && <NoItemsFound title={messages.submissionQueuePage.emptyState} />
        )}
      </Stack>
    </Stack>
  );
}
